import { gameView } from '../../../packages/domain/src/50-game-engine.mjs';

const SVG_NS = 'http://www.w3.org/2000/svg';

function node(ctx, tag, attributes, text = '') {
  const result = globalThis.document?.createElementNS
    ? document.createElementNS(SVG_NS, tag)
    : ctx.e(tag);
  for (const [name, value] of Object.entries(attributes)) result.setAttribute(name, String(value));
  if (text) result.textContent = text;
  return result;
}

/** Derive visible half-segments from accepted player paths, never the generator's
 * solution. Orthogonal neighbors reach the cell edge so zero-gap cells form one
 * uninterrupted ribbon. Malformed non-neighbor data has no visual shortcut. */
export function ribbonCellConnections(paths, index, columns) {
  const route = paths.find((line) => line.includes(index)) || [];
  const position = route.indexOf(index);
  if (position < 0) return [];
  return [route[position - 1], route[position + 1]].flatMap((neighbor) => {
    if (neighbor === undefined) return [];
    const dx = (neighbor % columns) - (index % columns);
    const dy = Math.floor(neighbor / columns) - Math.floor(index / columns);
    return Math.abs(dx) + Math.abs(dy) === 1 ? [[50 + dx * 50, 50 + dy * 50]] : [];
  });
}

let serial = 0;
function ribbonState(state, index) {
  const route = state.data.paths.find((line) => line.includes(index)) || [];
  return {
    active: state.data.active >= 0 && state.data.paths[state.data.active].at(-1) === index,
    connected:
      route.length >= 2 &&
      state.data.pairs.some((pair) => pair.includes(route[0]) && pair.includes(route.at(-1))),
  };
}

/** A local presentation identity permits retention of unchanged silk crossings.
 * Only accepted neighbour/path metadata is used, never puzzle witness routes. */
export function ribbonArtworkKey(state, cell, index) {
  const columns = gameView(state).columns;
  return JSON.stringify([
    columns,
    cell.colour,
    cell.endpoint,
    cell.glyph || '',
    ribbonCellConnections(state.data.paths, index, columns),
    ribbonState(state, index),
  ]);
}
function gradient(ctx, host, radial, stops) {
  const id = `ribbon-silk-${++serial}`;
  const defs = node(ctx, 'defs', {});
  // Straight centreline paths have zero-area object bounds. Tile-space paint
  // keeps their silk stroke as bright as bends instead of losing the gradient.
  const paint = node(
    ctx,
    radial ? 'radialGradient' : 'linearGradient',
    radial
      ? { id, cx: '32%', cy: '20%', r: '90%' }
      : { id, gradientUnits: 'userSpaceOnUse', x1: 0, y1: 0, x2: 100, y2: 100 },
  );
  for (const [offset, colour] of stops)
    paint.append(node(ctx, 'stop', { offset, 'stop-color': colour }));
  defs.append(paint);
  host.append(defs);
  return `url(#${id})`;
}
function ribbonPath(connections) {
  if (connections.length === 1) return `M50 50L${connections[0].join(' ')}`;
  const [a, b] = connections;
  if (a[0] === b[0] || a[1] === b[1]) return `M${a.join(' ')}L${b.join(' ')}`;
  const inner = (p) => [50 + (p[0] - 50) * 0.4, 50 + (p[1] - 50) * 0.4];
  return `M${a.join(' ')}L${inner(a).join(' ')}Q50 50 ${inner(b).join(' ')}L${b.join(' ')}`;
}
const endpointShapes = [
  'M50 20A30 30 0 1 1 49.99 20Z',
  'M50 14Q55 14 61 21L79 39Q88 50 79 61L61 79Q50 88 39 79L21 61Q12 50 21 39L39 21Q45 14 50 14Z',
  'M50 15Q55 15 59 23L82 69Q89 82 73 82H27Q11 82 18 69L41 23Q45 15 50 15Z',
  'M31 20H69Q80 20 80 31V69Q80 80 69 80H31Q20 80 20 69V31Q20 20 31 20Z',
  'M50 15L60 36L84 40L67 57L72 82L50 70L28 82L33 57L16 40L40 36Z',
  'M50 80Q15 55 19 34Q26 15 50 31Q74 15 81 34Q85 55 50 80Z',
];

/** Original silk paths with soft folded highlights and recognisable knot badges.
 * Exact edge connections stay authoritative; native cells retain input/focus. */
export function ribbonCellArtwork(ctx, state, cell, index, shape) {
  const art = node(ctx, 'svg', {
    class: 'game-piece-art game-ribbon-art',
    viewBox: '0 0 100 100',
    'aria-hidden': 'true',
    focusable: 'false',
    preserveAspectRatio: 'xMidYMid meet',
  });
  const connections = ribbonCellConnections(state.data.paths, index, gameView(state).columns);
  const { active, connected } = ribbonState(state, index);
  art.dataset.connected = String(connected);
  art.dataset.tip = String(active);
  art.setAttribute('data-ribbon-key', ribbonArtworkKey(state, cell, index));
  if (connections.length) {
    const d = ribbonPath(connections);
    const silk = gradient(ctx, art, false, [
      ['0%', 'color-mix(in srgb,var(--piece-colour) 60%,var(--game-white))'],
      ['30%', 'var(--piece-colour)'],
      ['55%', 'var(--piece-colour)'],
      ['100%', 'color-mix(in srgb,var(--piece-colour) 55%,var(--game-night))'],
    ]);
    for (const [width, stroke, opacity, cls] of [
      [43, 'var(--piece-colour)', connected ? 0.24 : 0.1, 'ribbon-aura'],
      [34, 'var(--game-night)', 0.7, 'ribbon-seam'],
      [28, silk, 1, 'ribbon-silk'],
      [17, 'var(--piece-colour)', 0.4, 'ribbon-body'],
      [3.5, 'var(--game-white)', connected ? 0.58 : 0.3, 'ribbon-light'],
    ])
      art.append(
        node(ctx, 'path', {
          d,
          class: cls,
          fill: 'none',
          stroke,
          'stroke-width': width,
          opacity,
          'stroke-linejoin': 'round',
          'stroke-linecap': 'round',
        }),
      );
    if (connected)
      art.append(
        node(ctx, 'path', {
          d,
          class: 'ribbon-flow',
          fill: 'none',
          stroke: 'var(--game-white)',
          'stroke-width': 5,
          'stroke-linecap': 'round',
          'stroke-dasharray': '7 120',
          opacity: 0,
        }),
      );
  }
  if (active) {
    art.append(
      node(ctx, 'circle', {
        class: 'ribbon-tip-halo',
        cx: 50,
        cy: 50,
        r: 35,
        fill: 'var(--piece-colour)',
        opacity: 0.16,
      }),
    );
    art.append(
      node(ctx, 'circle', {
        class: 'ribbon-contact',
        cx: 50,
        cy: 50,
        r: 24,
        fill: 'none',
        stroke: 'var(--game-white)',
        'stroke-width': 1.7,
        opacity: 0.5,
      }),
    );
  }
  if (cell.endpoint) {
    art.setAttribute('data-symbol', cell.glyph || shape);
    art.append(
      node(ctx, 'circle', {
        class: 'ribbon-endpoint-ring',
        cx: 50,
        cy: 50,
        r: 37,
        fill: 'none',
        stroke: 'var(--piece-colour)',
        'stroke-width': connected ? 3 : 1.5,
        opacity: connected ? 0.75 : 0.3,
      }),
    );
    const paint = gradient(ctx, art, true, [
      ['0%', 'var(--game-white)'],
      ['24%', 'color-mix(in srgb,var(--piece-colour) 65%,var(--game-white))'],
      ['62%', 'var(--piece-colour)'],
      ['100%', 'color-mix(in srgb,var(--piece-colour) 60%,var(--game-night))'],
    ]);
    art.append(
      node(ctx, 'path', {
        d: endpointShapes[cell.colour % 6],
        class: 'ribbon-knot',
        'data-endpoint-silhouette': cell.colour % 6,
        fill: paint,
        stroke: 'color-mix(in srgb,var(--piece-colour) 60%,var(--game-white))',
        'stroke-width': 1.5,
      }),
    );
    art.append(
      node(ctx, 'path', {
        d: 'M35 29Q45 22 59 27',
        fill: 'none',
        stroke: 'var(--game-white)',
        'stroke-width': 3,
        'stroke-linecap': 'round',
        opacity: 0.75,
      }),
    );
    art.append(
      node(
        ctx,
        'text',
        {
          x: 50,
          y: 53,
          fill: 'var(--game-night)',
          'font-size': 22,
          'font-weight': 800,
          'text-anchor': 'middle',
          'dominant-baseline': 'central',
        },
        cell.glyph || shape,
      ),
    );
  }
  return art;
}
